import { createSelector } from '@reduxjs/toolkit';
import { CommunicationsFormValues } from 'components/Communications/Communications.types';
import { AmountPlayedNotificationFormValues } from 'pages/PlayResponsabilityPage/partials/PlayResponsibly.types';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import {
  getAccountSessionAndLimitEndpoint,
  getGameBreakPeriodsEndpoint,
  getSelfLimitationPendingResourceId,
  getSelfLimitationPurposeResourceId,
  getSelfLimitationResourceId,
  getSelfLimitationTypeSuitedResourceId,
  getSummaryGameBreakEndpoint,
  getUserCommunicationsResourceId,
  postRevokeGameBreak,
  postSelfLimitationPendingResourceId,
  postSelfLimitationPurposeResourceId,
  postSelfLimitationResourceId,
  postSetGameBreak,
  postUserCommunicationsResourceId,
  selfLimitationEndpoint,
  selfLimitationPendingEndpoint,
  selfLimitationPurposeEndpoint,
  selfLimitationTypeSuitedEndpoint,
  setAccountGameSessionAndLimitEndpoint,
  userCommunicationsEndpoint,
} from 'services/playResponsibly/playResponsibly.config';
import {
  AmountPlayedNotificationFormStateConfig,
  CommunicationsFormStateConfig,
  postContextSensitiveAmountPlayedNotificationRequestData,
  postContextSensitiveCommunicationsRequestData,
} from 'services/playResponsibly/playResponsibly.helpers';
import {
  GameBreaksType,
  GameLimitsList,
  PausaDalGiocoState,
  PostSelfLimitationRequestObject,
  UserCommunication,
} from 'services/playResponsibly/playResponsibly.types';
import { AppState, ReduxSelector } from 'store/types';
import { v4 as uuidV4 } from 'uuid';
import {
  makeSelectAuthApiConfig,
  makeSelectResourceLoadingState,
} from '../api/api.selectors';

export const selectGetUserCommunicationsEndpointApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: getUserCommunicationsResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: userCommunicationsEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
  };
};

export function makeSelectCreateSubmitCommunicationsApiConfig({
  formValues,
}: {
  formValues: CommunicationsFormValues;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectSubmitCommunicationsApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    const CommunicationsStateConfig = {
      formValues,
    } as CommunicationsFormStateConfig;

    const contextSensitivePayloadData =
      postContextSensitiveCommunicationsRequestData(
        CommunicationsStateConfig as Required<CommunicationsFormStateConfig>,
      );

    const requestConfig = makeSelectAuthApiConfig({
      url: userCommunicationsEndpoint,
      method: 'POST',
      data: contextSensitivePayloadData,
      enrichRequestData: false,
    })(reduxState);

    return {
      resourceId: postUserCommunicationsResourceId,
      requestConfig,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectCreateSubmitAmountPlayedNotificationApiConfig({
  formValues,
}: {
  formValues: AmountPlayedNotificationFormValues;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectSubmitBalanceNotificationApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    const AmountPlayedNotificationStateConfig: AmountPlayedNotificationFormStateConfig =
      { formValues };

    const contextSensitivePayloadData =
      postContextSensitiveAmountPlayedNotificationRequestData(
        AmountPlayedNotificationStateConfig as Required<AmountPlayedNotificationFormStateConfig>,
      );

    return {
      resourceId: postUserCommunicationsResourceId,
      requestConfig: makeSelectAuthApiConfig({
        url: userCommunicationsEndpoint,
        method: 'POST',
        data: { communications: [contextSensitivePayloadData] },
        enrichRequestData: false,
      })(reduxState),
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectGetSummaryGameBreakApiConfig(): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetSummaryGameBreakApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: getSummaryGameBreakEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: getSummaryGameBreakEndpoint,
        method: 'GET',
        timeout: 25000,
        params: {
          requestId: uuidV4(),
          permanenti: false,
        },
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
    };
  };
}

export function makeSelectGetGameBreakPeriodsApiConfig(): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetGameBreakPeriodsApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: getGameBreakPeriodsEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: getGameBreakPeriodsEndpoint,
        method: 'GET',
        timeout: 25000,
        params: { requestId: uuidV4() },
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
    };
  };
}

export function makeSelectPostGameBreakPeriodsApiConfig(
  gameBreaks: GameBreaksType[],
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetGameBreakPeriodsApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: postSetGameBreak,
      requestConfig: makeSelectAuthApiConfig({
        url: postSetGameBreak,
        method: 'POST',
        timeout: 25000,
        data: {
          requestId: uuidV4(),
          gameBreaks: gameBreaks,
        },
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
    };
  };
}

export function makeSelectPostRevokePermanentGameBreakPeriodsApiConfig(
  gameBreaks: GameBreaksType[],
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectPostRevokePermanentGameBreakPeriodsApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: postRevokeGameBreak,
      requestConfig: makeSelectAuthApiConfig({
        url: postRevokeGameBreak,
        method: 'POST',
        timeout: 25000,
        data: {
          requestId: uuidV4(),
          gameBreaks: gameBreaks,
        },
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
    };
  };
}
export function makeSelectGetAccountGameSessionAndLimitApiConfig(): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetAccountGameSessionAndLimitApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: getAccountSessionAndLimitEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: getAccountSessionAndLimitEndpoint,
        method: 'GET',
        timeout: 25000,
        params: {
          requestId: uuidV4(),
          gameSessionType: 'CASINO',
        },
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectSetAccountGameSessionAndLimit({
  gameSessionLimit,
  insertDate,
}: {
  gameSessionLimit: number;
  insertDate: string;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectSetAccountGameSessionAndLimit(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: setAccountGameSessionAndLimitEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: setAccountGameSessionAndLimitEndpoint,
        method: 'POST',
        data: {
          requestId: uuidV4(),
          gameSessionLimit: gameSessionLimit,
          insertDate: insertDate,
        },
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
    };
  };
}

export function selectGetSelfLimitationApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  return {
    resourceId: getSelfLimitationResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
    invalidateIfPresent: true,
  };
}

export function makeSelectPostSelfLimitationApiConfig(
  accountCode: string,
  amount: number,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  const requestObject: PostSelfLimitationRequestObject = {
    selfLimitationList: [
      {
        typeCode: 'DW',
        accountCode,
        amount,
      },
    ],
  };

  return (reduxState: AppState) => ({
    resourceId: postSelfLimitationResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationEndpoint,
      method: 'POST',
      data: { requestObject },
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
  });
}

export function selectGetSelfLimitationPendingApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  return {
    resourceId: getSelfLimitationPendingResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationPendingEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
    invalidateIfPresent: true,
  };
}

export function selectGetSelfLimitationPurpopseApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  return {
    resourceId: getSelfLimitationPurposeResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationPurposeEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
    invalidateIfPresent: true,
  };
}

export function selectGetSelfLimitationTypeSuitedApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  return {
    resourceId: getSelfLimitationTypeSuitedResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationTypeSuitedEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
    invalidateIfPresent: true,
  };
}

export function makeSelectPostSelfLimitationPurposeApiConfig(
  amount: number,
  accept: boolean,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return (reduxState: AppState) => ({
    resourceId: postSelfLimitationPurposeResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationPurposeEndpoint,
      method: 'POST',
      data: {
        requestObject: {
          typeCode: 'DW',
          amount,
          accept,
        },
      },
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
  });
}

export function makeSelectPostSelfLimitationPendingApiConfig(
  amount: number,
  accept: boolean,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return (reduxState: AppState) => ({
    resourceId: postSelfLimitationPendingResourceId,
    requestConfig: makeSelectAuthApiConfig({
      url: selfLimitationPendingEndpoint,
      method: 'POST',
      data: {
        requestObject: {
          typeCode: 'DW',
          amount,
          accept,
        },
      },
      enrichRequestData: false,
    })(reduxState),
    skipDefaultServerErrorHandling: true,
  });
}

export const selectGiocaGiustoState = (reduxState: AppState) =>
  reduxState.giocaGiusto;

export const selectUserCommunications: ReduxSelector<UserCommunication[]> = (
  reduxState: AppState,
) => selectGiocaGiustoState(reduxState).userCommunications;

export const selectSummaryGameBreak: ReduxSelector<PausaDalGiocoState> = (
  reduxState: AppState,
) => selectGiocaGiustoState(reduxState).pausaDalGioco;

export const selectAccountGameSessionAndLimit: ReduxSelector<GameLimitsList> = (
  reduxState: AppState,
) => selectGiocaGiustoState(reduxState).gameLimitsList;

export const selectSetAccountGameSessionAndLoadState =
  makeSelectResourceLoadingState(setAccountGameSessionAndLimitEndpoint);

export const selectAmountPlayedNotificationLoadState =
  makeSelectResourceLoadingState(getUserCommunicationsResourceId);

export const selectGetSummaryGameBreakLoadState =
  makeSelectResourceLoadingState(getSummaryGameBreakEndpoint);

export const selectGetGameBreakPeriodsLoadState =
  makeSelectResourceLoadingState(getGameBreakPeriodsEndpoint);

export const selectGetAccountGameSessionAndLimitLoadState =
  makeSelectResourceLoadingState(getAccountSessionAndLimitEndpoint);

export const selectGetSelfLimitationLoadState = makeSelectResourceLoadingState(
  getSelfLimitationResourceId,
);

export const selectGetSelfLimitationPendingLoadState =
  makeSelectResourceLoadingState(getSelfLimitationPendingResourceId);

export const selectGetSelfLimitationPurposeLoadState =
  makeSelectResourceLoadingState(getSelfLimitationPurposeResourceId);

export const selectGetSelfLimitationTypeSuitedLoadState =
  makeSelectResourceLoadingState(getSelfLimitationTypeSuitedResourceId);

export const selectPausaDalGiocoFeedbackMessage = (reduxState: AppState) =>
  reduxState.giocaGiusto.pausaDalGioco.feedbackMessage;

export const selectSelfLimitation = (reduxState: AppState) =>
  selectGiocaGiustoState(reduxState).selfLimitation;

export const selectCurrentSelfLimitation = createSelector(
  selectGiocaGiustoState,
  (reduxState) =>
    reduxState.selfLimitation.find((element) => !!element.currentSelfLimitation)
      ?.currentSelfLimitation,
);

export const selectNextSelfLimitation = createSelector(
  selectGiocaGiustoState,
  (reduxState) =>
    reduxState.selfLimitation.find((element) => !!element.nextSelfLimitation)
      ?.nextSelfLimitation,
);

export const selectSelfLimitationPending = (reduxState: AppState) =>
  selectGiocaGiustoState(reduxState).selfLimitationPending;

export const selectSelfLimitationPurpose = (reduxState: AppState) =>
  selectGiocaGiustoState(reduxState).selfLimitationPurpose;

export const selectSelfLimitationTypeSuited = (reduxState: AppState) =>
  selectGiocaGiustoState(reduxState).selfLimitationTypeSuited;

export const selectSelfLimitationPostLoading = (reduxState: AppState) =>
  selectGiocaGiustoState(reduxState).selfLimitationPostLoading;
